import { useEffect, useRef, useState } from "react";

import {
  getPreviewFromCache,
  savePreviewToCache,
} from "~/lib/cache/formPreviewCache";
import { getLocalStorageItem } from "~/lib/localStorage";
import { createFormSubmissionPreview } from "~/lib/payload/forms";
import type { FormPageType } from "~/types";
import { canUserNavigateToStep } from "~/util/formStepper";

interface Args {
  tempId?: string;
  templateId?: string;
  pages?: FormPageType[];
  isSerialLoading?: boolean;
}

const useCreateFormSubmissionPreview = ({
  tempId,
  templateId,
  pages,
  isSerialLoading,
}: Args = {}) => {
  // Use ref to persist preview data across rerenders
  const previewRef = useRef<string | null>(null);

  // Start with false to avoid showing loading when we might have cache
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingCache, setIsCheckingCache] = useState(true);
  const [isUsingCache, setIsUsingCache] = useState(false);

  // Initialize preview with ref value to prevent null on rerenders
  const [preview, setPreview] = useState<string | null>(previewRef.current);
  const [formState, setFormState] = useState(null);
  const [error, setError] = useState("");
  const [canPreview, setCanPreview] = useState(false);
  const [stepToRedirect, setStepToRedirect] = useState<number | null>(null);

  // Helper to update both ref and state
  const updatePreview = (value: string | null) => {
    previewRef.current = value;
    setPreview(value);
  };

  const stringifiedPages = JSON.stringify(pages);

  useEffect(() => {
    const fetchData = async () => {
      const pages = JSON.parse(stringifiedPages);

      try {
        if (!tempId || !templateId) {
          setIsCheckingCache(false);
          return;
        }

        const savedFormState = getLocalStorageItem(tempId);

        if (!savedFormState) {
          setIsCheckingCache(false);
          setIsLoading(false);
          setCanPreview(false);
          return;
        }

        setFormState(savedFormState);

        const { incompletePageIndex, canAccess } = canUserNavigateToStep({
          step: "preview",
          pages,
          formState: savedFormState,
        });

        // If the user has not completed for form, stop the preview process
        // and return the step they need to complete.
        if (!canAccess) {
          setCanPreview(false);
          setStepToRedirect(incompletePageIndex);
          setIsCheckingCache(false);
          setIsLoading(false);
          return;
        }

        setCanPreview(true);

        // Check IndexedDB cache first
        const cachedPreview = await getPreviewFromCache(tempId, savedFormState);

        if (cachedPreview) {
          // Use cached preview immediately
          updatePreview(cachedPreview);
          setIsUsingCache(true);
          setIsCheckingCache(false);
          setIsLoading(false);
          return;
        }

        // No cache found, now we need to show loading and make API call
        setIsCheckingCache(false);
        setIsLoading(true);
        setIsUsingCache(false);

        // The form state needs to be flattened before sending to the API.
        // This is not done when saving to temporary storage as we can leave as is.
        // Whenever sending to the backend we need to flatten the state.
        // And when we receive the state back from the backend we need to unflatten.
        const response = await createFormSubmissionPreview({
          template: templateId,
          submission: savedFormState,
        });

        if (response.success) {
          updatePreview(response.data as string);

          // Save to IndexedDB cache for next time
          await savePreviewToCache(
            tempId,
            response.data as string,
            savedFormState,
            templateId
          );
        } else {
          setError("Unable to generate a preview.");
        }

        setIsLoading(false);
      } catch {
        setError("An unknown error occurred");
        setIsLoading(false);
        setIsCheckingCache(false);
      }
    };

    if (!isSerialLoading) {
      fetchData();
    }
  }, [tempId, templateId, stringifiedPages, isSerialLoading]);

  return {
    isLoading: isSerialLoading || isLoading || isCheckingCache,
    formState,
    preview,
    stepToRedirect,
    canPreview,
    error,
    isUsingCache,
    isCheckingCache,
  };
};

export default useCreateFormSubmissionPreview;
